import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid';
import {
  Search,
  PersonAdd,
  Block,
  CheckCircle,
  Cancel,
  MoreVert,
  Visibility,
  School,
  Download,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { userService } from '../services/userService';
import { User, UserRole, UserStatus, UserFilters } from '../types';
import { defaultDataGridStyles } from '../utils';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 2, pt: 1 }}>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );
}

// ============================================================================
// USER MANAGEMENT PAGE COMPONENT
// ============================================================================

const UserManagementPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>([]);

  // Filter states
  const [filters, setFilters] = useState<UserFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [roleChangeDialog, setRoleChangeDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });
  
  const [userDetailDialog, setUserDetailDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  // Menu state
  const [actionMenu, setActionMenu] = useState<{
    anchorEl: HTMLElement | null;
    user: User | null;
  }>({ anchorEl: null, user: null });

  // Get pending applications count
  const pendingApplicationsCount = users.filter(user => user.applicationStatus === 'pending').length;

  const handleExportCSV = () => {
    const headers = ['Email', 'Username', 'Role', 'Signup Date', 'Application Status', 'Status', 'Videos Watched', 'Quizzes Completed'];
    const rows = users.map(u => [
      u.email,
      u.name,
      u.role,
      format(new Date(u.registrationDate), 'yyyy-MM-dd'),
      u.applicationStatus || '-',
      u.status,
      String(u.totalVideos || 0),
      String(u.totalQuizzes || 0),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `learnova-users-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    enqueueSnackbar('Users exported to CSV', { variant: 'success' });
  };

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers(
        currentPage, // Supabase uses 0-based pagination
        pageSize,
        { ...filters, searchQuery }
      );
      
      if (response.success) {
        setUsers(response.data.data);
        setTotalUsers(response.data.pagination.totalItems);
      } else {
        enqueueSnackbar(response.error || 'Failed to fetch users', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters, searchQuery, enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ============================================================================
  // USER ACTIONS
  // ============================================================================

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      const response = await userService.updateUserRole(userId, newRole);
      if (response.success) {
        enqueueSnackbar(`User role updated to ${newRole}`, { variant: 'success' });
        fetchUsers();
      } else {
        enqueueSnackbar(response.error || 'Failed to update role', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update user role', { variant: 'error' });
    }
  };

  const handleStatusChange = async (userId: string, status: UserStatus, reason?: string) => {
    try {
      const response = await userService.updateUserStatus(userId, status, reason);
      
      if (response.success) {
        enqueueSnackbar(response.message || `User status updated to ${status}`, { variant: 'success' });
        fetchUsers();
      } else {
        enqueueSnackbar(response.error || 'Failed to update user status', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update user status', { variant: 'error' });
    }
  };

  const handleApplicationStatusChange = async (userId: string, status: 'approved' | 'rejected', reason?: string) => {
    try {
      const response = await userService.updateApplicationStatus(userId, status, reason);
      
      if (response.success) {
        enqueueSnackbar(response.message || `Application ${status} successfully`, { variant: 'success' });
        fetchUsers();
      } else {
        enqueueSnackbar(response.error || 'Failed to update application status', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update application status', { variant: 'error' });
    }
  };

  const handleBulkPromoteToCreators = async () => {
    const selectedIds = Array.from(selectedUsers);
    if (selectedIds.length === 0) return;

    try {
      const response = await userService.bulkApproveCreators(selectedIds as string[]);
      
      if (response.success) {
        enqueueSnackbar(
          response.message || `Successfully promoted ${selectedIds.length} users to creators`,
          { variant: 'success' }
        );
        setSelectedUsers([]);
        fetchUsers();
      } else {
        enqueueSnackbar(response.error || 'Failed to promote users', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to promote users to creators', { variant: 'error' });
    }
  };

  const handleBulkApproveApplications = async () => {
    const selectedIds = Array.from(selectedUsers);
    if (selectedIds.length === 0) return;

    try {
      const response = await userService.bulkApproveApplications(selectedIds as string[]);
      
      if (response.success) {
        enqueueSnackbar(
          response.message || `Successfully approved ${selectedIds.length} applications`,
          { variant: 'success' }
        );
        setSelectedUsers([]);
        fetchUsers();
      } else {
        enqueueSnackbar(response.error || 'Failed to approve applications', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to approve applications', { variant: 'error' });
    }
  };

  // ============================================================================
  // TABLE CONFIGURATION
  // ============================================================================

  const columns: GridColDef[] = [
    {
      field: 'profileImage',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt={params.row.name}
          sx={{ width: 32, height: 32 }}
        >
          {params.row.name?.charAt(0)?.toUpperCase()}
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ID: {params.row.id.slice(0, 8)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'creator' ? 'primary' : params.value === 'admin' ? 'secondary' : 'default'}
          variant={params.value === 'creator' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'applicationStatus',
      headerName: 'Application Status',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => {
        if (params.row.role !== 'creator' || !params.value) return <Typography variant="body2">-</Typography>;
        return (
          <Chip
            label={params.value === 'pending' ? 'Pending Approval' : params.value}
            size="small"
            color={
              params.value === 'approved' ? 'success' :
              params.value === 'pending' ? 'warning' :
              params.value === 'rejected' ? 'error' :
              'default'
            }
            sx={{
              fontWeight: params.value === 'pending' ? 'bold' : 'normal'
            }}
          />
        );
      },
    },
    {
      field: 'socialMediaHandle',
      headerName: 'Social Media',
      width: 120,
      renderCell: (params) => {
        if (!params.value || params.row.role !== 'creator') return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'expertiseAreas',
      headerName: 'Expertise',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value || !Array.isArray(params.value) || params.value.length === 0) {
          return <Typography variant="body2">-</Typography>;
        }
        const displayAreas = params.value.slice(0, 2);
        const remaining = params.value.length - 2;
        return (
          <Box>
            {displayAreas.map((area, index) => (
              <Chip
                key={index}
                label={area}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            ))}
            {remaining > 0 && (
              <Chip
                label={`+${remaining} more`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === 'active' ? 'success' :
            params.value === 'inactive' ? 'error' :
            'default'
          }
        />
      ),
    },
    {
      field: 'registrationDate',
      headerName: 'Registered',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {format(new Date(params.value), 'MMM dd, yyyy')}
        </Typography>
      ),
    },
    {
      field: 'totalVideos',
      headerName: 'Videos Watched',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'totalQuizzes',
      headerName: 'Quizzes Done',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => setUserDetailDialog({ open: true, user: params.row })}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, user: params.row })}
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          User Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => {/* TODO: Add user creation */}}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Pending Applications Alert */}
      {pendingApplicationsCount > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setFilters({ ...filters, applicationStatus: 'pending' })}
            >
              View Pending
            </Button>
          }
          sx={{ mb: 2 }}
        >
          <strong>{pendingApplicationsCount} creator applications</strong> are pending approval.
        </Alert>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role || ''}
                  label="Role"
                  onChange={(e) => setFilters({ ...filters, role: e.target.value as UserRole })}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="student">Students</MenuItem>
                  <MenuItem value="creator">Creators</MenuItem>
                  <MenuItem value="admin">Admins</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as UserStatus })}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Application</InputLabel>
                <Select
                  value={filters.applicationStatus || ''}
                  label="Application"
                  onChange={(e) => setFilters({ ...filters, applicationStatus: e.target.value as any })}
                >
                  <MenuItem value="">All Applications</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              {Array.from(selectedUsers).length > 0 && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={handleBulkPromoteToCreators}
                    startIcon={<CheckCircle />}
                    size="small"
                  >
                    Promote Selected ({Array.from(selectedUsers).length})
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={handleBulkApproveApplications}
                    startIcon={<School />}
                    size="small"
                  >
                    Approve Applications ({Array.from(selectedUsers).length})
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={totalUsers}
          paginationModel={{ page: currentPage, pageSize: pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) {
              setCurrentPage(0);
            } else {
              setCurrentPage(model.page);
            }
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={setSelectedUsers}
          rowSelectionModel={selectedUsers}
          slots={{ toolbar: CustomToolbar }}
          sx={{ ...defaultDataGridStyles, height: 'calc(100vh - 320px)', minHeight: 500 }}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={() => setActionMenu({ anchorEl: null, user: null })}
      >
        {actionMenu.user?.role === 'student' && (
          <MenuItem
            onClick={() => {
              if (actionMenu.user) {
                setRoleChangeDialog({
                  open: true,
                  user: actionMenu.user,
                });
              }
              setActionMenu({ anchorEl: null, user: null });
            }}
          >
            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
            <ListItemText>Promote to Creator</ListItemText>
          </MenuItem>
        )}
        {actionMenu.user?.applicationStatus === 'pending' && (
          <>
            <MenuItem
              onClick={() => {
                if (actionMenu.user) {
                  handleApplicationStatusChange(actionMenu.user.id, 'approved');
                }
                setActionMenu({ anchorEl: null, user: null });
              }}
            >
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText>Approve Application</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (actionMenu.user) {
                  handleApplicationStatusChange(actionMenu.user.id, 'rejected', 'Application rejected by admin');
                }
                setActionMenu({ anchorEl: null, user: null });
              }}
            >
              <ListItemIcon><Cancel color="error" /></ListItemIcon>
              <ListItemText>Reject Application</ListItemText>
            </MenuItem>
          </>
        )}
        <MenuItem
          onClick={() => {
            if (actionMenu.user) {
              const newStatus = actionMenu.user.status === 'active' ? 'inactive' : 'active';
              handleStatusChange(actionMenu.user.id, newStatus as UserStatus);
            }
            setActionMenu({ anchorEl: null, user: null });
          }}
        >
          <ListItemIcon>
            {actionMenu.user?.status === 'active' ? (
              <Block color="error" />
            ) : (
              <CheckCircle color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {actionMenu.user?.status === 'active' ? 'Deactivate User' : 'Activate User'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Role Change Dialog */}
      <Dialog
        open={roleChangeDialog.open}
        onClose={() => setRoleChangeDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Promote to Creator</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Promote <strong>{roleChangeDialog.user?.name}</strong> to content creator?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This grants permission to upload videos and create courses.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (roleChangeDialog.user) {
                handleRoleChange(roleChangeDialog.user.id, 'creator');
              }
              setRoleChangeDialog({ open: false, user: null });
            }}
          >
            Promote to Creator
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog
        open={userDetailDialog.open}
        onClose={() => setUserDetailDialog({ open: false, user: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            User Details
            {userDetailDialog.user?.role === 'creator' && userDetailDialog.user?.applicationStatus === 'pending' && (
              <Chip
                label="Pending Review"
                color="warning"
                size="small"
                icon={<School />}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {userDetailDialog.user && (
            <Grid container spacing={3}>
              {/* Left Column - Basic Info */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Avatar
                    src={userDetailDialog.user.profileImage}
                    sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
                  >
                    {userDetailDialog.user.name?.charAt(0)}
                  </Avatar>
                  <Typography variant="h6">{userDetailDialog.user.name}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 1 }}>{userDetailDialog.user.email}</Typography>
                  <Chip
                    label={userDetailDialog.user.role}
                    color={userDetailDialog.user.role === 'creator' ? 'primary' : 'default'}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Registered {format(new Date(userDetailDialog.user.registrationDate), 'PPP')}
                  </Typography>
                  {userDetailDialog.user.lastLoginDate && (
                    <Typography variant="body2" color="text.secondary">
                      Last login {format(new Date(userDetailDialog.user.lastLoginDate), 'PPP')}
                    </Typography>
                  )}
                </Card>

                {/* Activity Stats */}
                <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Activity Statistics</Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Videos:</Typography>
                    <Typography variant="body2" fontWeight="medium">{userDetailDialog.user.totalVideos || 0}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Quizzes:</Typography>
                    <Typography variant="body2" fontWeight="medium">{userDetailDialog.user.totalQuizzes || 0}</Typography>
                  </Box>
                  {userDetailDialog.user.totalViews && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Views:</Typography>
                      <Typography variant="body2" fontWeight="medium">{userDetailDialog.user.totalViews.toLocaleString()}</Typography>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Right Column - Detailed Info */}
              <Grid item xs={12} md={8}>
                {/* Creator Application Details */}
                {userDetailDialog.user.role === 'creator' && (
                  <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Creator Application Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Application Status</Typography>
                        <Chip
                          label={userDetailDialog.user.applicationStatus || 'Unknown'}
                          color={
                            userDetailDialog.user.applicationStatus === 'approved' ? 'success' :
                            userDetailDialog.user.applicationStatus === 'pending' ? 'warning' :
                            userDetailDialog.user.applicationStatus === 'rejected' ? 'error' :
                            'default'
                          }
                          sx={{ mb: 2 }}
                        />
                        {userDetailDialog.user.applicationDate && (
                          <Typography variant="body2" color="text.secondary">
                            Applied {format(new Date(userDetailDialog.user.applicationDate), 'PPP')}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Social Media</Typography>
                        {userDetailDialog.user.socialMediaHandle ? (
                          <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace', mb: 2 }}>
                            {userDetailDialog.user.socialMediaHandle}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Not provided
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Areas of Expertise</Typography>
                        <Box sx={{ mb: 2 }}>
                          {userDetailDialog.user.expertiseAreas && userDetailDialog.user.expertiseAreas.length > 0 ? (
                            userDetailDialog.user.expertiseAreas.map((area, index) => (
                              <Chip
                                key={index}
                                label={area}
                                variant="outlined"
                                size="small"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No expertise areas specified
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {userDetailDialog.user.bio && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>Professional Bio</Typography>
                          <Typography variant="body2" sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap'
                          }}>
                            {userDetailDialog.user.bio}
                          </Typography>
                        </Grid>
                      )}

                      {userDetailDialog.user.applicationNotes && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom color="warning.main">
                            Admin Notes
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            p: 2, 
                            bgcolor: 'warning.50', 
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'warning.200'
                          }}>
                            {userDetailDialog.user.applicationNotes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                )}

                {/* Additional Information */}
                {(userDetailDialog.user.credentials || userDetailDialog.user.experience || userDetailDialog.user.website) && (
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Additional Information</Typography>
                    
                    {userDetailDialog.user.credentials && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>Credentials</Typography>
                        <Typography variant="body2">{userDetailDialog.user.credentials}</Typography>
                      </Box>
                    )}

                    {userDetailDialog.user.experience && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>Experience</Typography>
                        <Typography variant="body2">{userDetailDialog.user.experience}</Typography>
                      </Box>
                    )}

                    {userDetailDialog.user.website && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>Website</Typography>
                        <Typography variant="body2" color="primary" component="a" href={userDetailDialog.user.website} target="_blank">
                          {userDetailDialog.user.website}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {userDetailDialog.user?.role === 'creator' && userDetailDialog.user?.applicationStatus === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (userDetailDialog.user) {
                    handleApplicationStatusChange(userDetailDialog.user.id, 'approved', 'Application approved by admin');
                  }
                  setUserDetailDialog({ open: false, user: null });
                }}
                startIcon={<CheckCircle />}
              >
                Approve Application
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  if (userDetailDialog.user) {
                    handleApplicationStatusChange(userDetailDialog.user.id, 'rejected', 'Application rejected by admin');
                  }
                  setUserDetailDialog({ open: false, user: null });
                }}
                startIcon={<Cancel />}
              >
                Reject Application
              </Button>
            </>
          )}
          <Button onClick={() => setUserDetailDialog({ open: false, user: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage; 
